export type SemanticScore = "positive" | "negative" | "neutral" | "unknown";

interface SemanticScoreThresholds {
  /** Threshold at which a value >= this value should be considered "positive"
   * (or <= this value if inverted) */
  positive: number;
  /** Threshold at which a value >= this value (but lower than the positive threshold)
   * should be considered "neutral" (or <= this value if inverted) */
  neutral?: number;
  /** Optional max value; if defined, values above this value will be considered "unknown" */
  max?: number;
  /** Optional min value; if defined, values below this value will be considered "unknown" */
  min?: number;
}

export const DEFAULT_METRIC_THRESHOLDS = {
  positive: 80,
  neutral: 70,
  max: 100,
  min: 50,
};

interface SemanticScoreOptions {
  /** If false, will apply scores in ascending order ("negative" < "neutral" < "positive").
   * If true, will apply scores id a descending order ("positive" < "neutral" < "negative").
   * Defaults to false */
  inverted?: boolean;
  /**
   * Only supported for inverted scales. If true, will treat each threshold value as the maximum
   * value that qualifies for that semantic score. If false, will treat each threshold value as
   * the value at which it bumps the score into the next threshold.
   * Defaults to true.
   * */
  inclusive?: boolean;
}

const isGreaterThan = (value: number, comparator: number, inclusive = true) => {
  // seems backwards, but since we're backing into the score, this is correct
  if (inclusive) return value > comparator;
  return value >= comparator;
};

const getInvertedSemanticScore = (
  score: number | undefined | null,
  thresholds: SemanticScoreThresholds = DEFAULT_METRIC_THRESHOLDS,
  opts?: SemanticScoreOptions
): SemanticScore => {
  if (score == null) return "unknown";

  const isImpossiblyHigh = thresholds.max != null && score > thresholds.max;
  if (isImpossiblyHigh) return "unknown";

  const isBinaryScore = thresholds.neutral == null;

  const isAboveNeutral =
    !isBinaryScore &&
    isGreaterThan(
      score,
      thresholds.neutral ?? thresholds.positive,
      opts?.inclusive
    );

  const isAbovePositive = isGreaterThan(
    score,
    thresholds.positive,
    opts?.inclusive
  );

  if ((isBinaryScore && isAbovePositive) || isAboveNeutral) {
    return "negative";
  }

  if (isAbovePositive) return "neutral";

  // if there's no min value, we can assume that anything below the "positive" threshold is
  // safe to be considered "positive". if there *is* a min value, that is the threshold at
  // which the value is no longer "positive" in the sense that it's an impossibly low value
  if (thresholds.min == null) return "positive";

  return score >= thresholds.min ? "positive" : "unknown";
};

/** Returns the SemanticScore ("positive", "neutral", etc.) given a set of thresholds.
 * @param score raw score value
 * @param thresholds map of thresholds to apply to the score
 * @param opts Options to apply to semantic score assignments
 * */
export const getSemanticScore = (
  score: number | undefined | null,
  thresholds: SemanticScoreThresholds = DEFAULT_METRIC_THRESHOLDS,
  opts?: SemanticScoreOptions
): SemanticScore => {
  if (score == null) return "unknown";

  if (opts?.inverted) return getInvertedSemanticScore(score, thresholds, opts);

  const isImpossiblyLow = thresholds.min != null && score < thresholds.min;
  if (isImpossiblyLow) return "unknown";

  const isBinaryScore = thresholds.neutral == null;

  const isBelowNeutral =
    !isBinaryScore && score < (thresholds.neutral ?? thresholds.positive);
  const isBelowPositive = score < thresholds.positive;

  if ((isBinaryScore && isBelowPositive) || isBelowNeutral) {
    return "negative";
  }

  if (isBelowPositive) return "neutral";

  // if there's no max value, we can assume that anything passing the "positive" threshold is
  // safe to be considered "positive". if there *is* a max value, that is the threshold at
  // which the value is no longer "positive" in the sense that it's an impossibly high value
  if (thresholds.max == null) return "positive";

  return score <= thresholds.max ? "positive" : "unknown";
};
